import React, { useEffect, useRef } from 'react';

/**
 * In-feed AdSense unit rendered between chat turns (after every Nth substantial
 * AI response — see Chatty.js). Manual unit "chatty-infeed".
 *
 * The one SPA gotcha: adsbygoogle.push() throws "already have ads in them" if it
 * runs twice on the same <ins>, and the chat re-renders constantly while
 * streaming. Guarded by: React.memo + a stable per-message key (set by the
 * caller) so this never remounts, plus a `pushed` ref so push() fires exactly
 * once per <ins> (also absorbs StrictMode's dev double-mount).
 */
const AD_CLIENT = 'ca-pub-6432312046086417';
const AD_SLOT = '4261506722';           // In-feed unit "chatty-infeed" — the chat is a feed of messages
const AD_LAYOUT_KEY = '-cp-1v+69-ef+3g';

/**
 * Master guard — single place to turn in-chat ads OFF. Three layers:
 *   1. Global build switch: set REACT_APP_CHAT_ADS_ENABLED=false in Amplify → ALL
 *      ads gone on next deploy (use when the app goes fully paid).
 *   2. Per-browser instant off: localStorage['chatty:adsOff']='1' (no rebuild) —
 *      handy for the owner or a future in-app "hide ads" toggle.
 *   3. Paid-tier gate: members on an ad-free tier never see ads.
 * Adjust AD_FREE_TIERS to your real membership values once the credits model lands.
 */
export const CHAT_ADS_ENABLED =
  (process.env.REACT_APP_CHAT_ADS_ENABLED || 'true') !== 'false';

const AD_FREE_TIERS = ['paid', 'pro', 'premium', 'credit', 'subscriber'];

export function chatAdsEnabledFor(user) {
  if (!CHAT_ADS_ENABLED) return false;
  try { if (localStorage.getItem('chatty:adsOff') === '1') return false; } catch (e) {}
  const role = user && user['custom:membership'];
  if (AD_FREE_TIERS.includes(role)) return false;
  return true;
}

function InChatAd() {
  const pushed = useRef(false);
  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      /* ad blocker, offline, or loader not ready — non-fatal, leave the slot empty */
    }
  }, []);

  return (
    <div className="w-full flex justify-start" style={{ marginBottom: '2.5rem' }}>
      <div className="in-chat-ad" style={{ width: '100%', maxWidth: '80%', padding: '0 0.5rem' }}>
        <div
          style={{
            fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase',
            opacity: 0.45, marginBottom: '4px', color: 'var(--text-secondary, #888)',
          }}
        >
          Sponsored
        </div>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key={AD_LAYOUT_KEY}
          data-ad-client={AD_CLIENT}
          data-ad-slot={AD_SLOT}
        />
      </div>
    </div>
  );
}

export default React.memo(InChatAd);
